import React, { useEffect, useState } from "react";
import { ShieldAlert, Check } from "lucide-react";
import { useSettings } from "../lib/settings";

const STORAGE_KEY = "ts_privacy_accepted_v2";

export default function PrivacyModal() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (!accepted) setOpen(true);
  }, []);

  if (!open) return null;

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 fade-up overflow-y-auto"
      data-testid="privacy-modal"
    >
      <div className="max-w-2xl w-full glass rounded-3xl p-8 md:p-10 border border-white/10 cyan-glow my-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="shrink-0 w-12 h-12 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/40 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)] mb-2">Términos & Política de Privacidad</p>
            <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight">
              Bienvenido a {settings.agency_name}
            </h2>
          </div>
        </div>

        <div className="space-y-4 text-sm text-neutral-300 leading-relaxed mb-6 max-h-[40vh] overflow-y-auto pr-2">
          <div className="rounded-2xl border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-5">
            <p className="font-display text-base md:text-lg font-bold text-[#FF3B30] uppercase tracking-wider leading-snug">
              ⚠ Esta aplicación es una herramienta de apoyo que depende del acceso a internet.
              No sustituye a los servicios de emergencia oficiales. Siempre marca al 911 para
              emergencias inmediatas que pongan en riesgo la vida.
            </p>
          </div>

          <ol className="list-decimal list-outside pl-5 space-y-3 marker:text-[var(--brand-cyan,#FF85AC)]">
            <li>
              <b className="text-white">Proveedor de la plataforma.</b> Esta aplicación es operada
              tecnológicamente por <b className="text-[var(--brand-cyan,#FF85AC)]">Atik Tech</b>, en nombre de la
              agencia de viajes contratante. Atik Tech actúa como proveedor de software y servicios
              en la nube; la agencia es responsable de los servicios turísticos.
            </li>
            <li>
              <b className="text-white">Comunicaciones de marketing.</b> Autorizas a {settings.agency_name}{" "}
              (la agencia) y a sus aliados comerciales — incluyendo, entre otros, proveedores de
              <b> eSIM</b>, <b>seguros de viaje</b>, <b>asistencia médica</b> y operadores
              turísticos — a enviarte promociones, ofertas y comunicaciones relevantes por email,
              WhatsApp, SMS o notificaciones push.
            </li>
            <li>
              <b className="text-white">Análisis anónimo de consumo.</b> Autorizas el procesamiento
              y análisis <b>anónimo y agregado</b> de tus patrones de consumo, ubicaciones
              visitadas, intereses y comportamiento dentro de la app para fines estadísticos y de
              segmentación, que pueden ser compartidos con socios comerciales sin identificarte
              personalmente.
            </li>
            <li>
              <b className="text-white">Limitación de responsabilidad SOS.</b> El botón SOS, la
              ubicación GPS y la mensajería son herramientas de apoyo dependientes de conectividad
              a internet y permisos del dispositivo. No funcionan sin red. Su disponibilidad no es
              garantizada ni equivalente a un servicio oficial de emergencia. Atik Tech y {settings.agency_name}{" "}
              <b>no son responsables</b> por daños derivados de la imposibilidad de uso
              durante una emergencia.
            </li>
            <li>
              <b className="text-white">Datos recopilados.</b> Recopilamos: nombre, contacto,
              ubicación al activar SOS, miembros de tu grupo de emergencia, mensajes/imágenes
              compartidas y eventos de uso de la app.
            </li>
          </ol>
        </div>

        <label className="flex items-start gap-3 mb-6 cursor-pointer select-none" data-testid="privacy-checkbox">
          <span
            onClick={() => setAgree(!agree)}
            className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
              agree ? "bg-[var(--brand-cyan,#FF85AC)] border-[var(--brand-cyan,#FF85AC)]" : "border-white/30"
            }`}
          >
            {agree && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
          </span>
          <span className="text-sm text-neutral-300" onClick={() => setAgree(!agree)}>
            He leído los términos y entiendo las limitaciones (SOS, conectividad, marketing, análisis anónimo).
          </span>
        </label>

        <button
          disabled={!agree}
          onClick={accept}
          data-testid="privacy-accept-btn"
          className={`w-full py-3.5 rounded-full font-semibold tracking-wide transition-all ${
            agree
              ? "bg-[var(--brand-cyan,#FF85AC)] text-black hover:bg-[#FF9DC0] cyan-glow"
              : "bg-neutral-900 text-neutral-600 cursor-not-allowed"
          }`}
        >
          Acepto y entiendo las limitaciones
        </button>

        <p className="mt-4 text-[10px] text-neutral-600 text-center uppercase tracking-[0.25em]">
          Plataforma operada por Atik Tech · v1.2
        </p>
      </div>
    </div>
  );
}
